/*
 * LegStyle: Choose Your Cover, <legstyle-choose-cover>
 *
 * Adds behaviour on top of the server-rendered markup of sections/legstyle-choose-cover.liquid:
 *   - theme tabs (real keyboard tabs: arrow keys, Home, End);
 *   - a card tap shows that design on the cover (a card is still a link to the product page, so with no
 *     JavaScript, or with Ctrl or Cmd held, it opens the product page);
 *   - colour radios recolour the cover;
 *   - the choice summary with the exact order-form words, a copy button and a shareable link
 *     (?design=<product handle>&colour=<code>), which this page also reads when it opens.
 * It reads only the JSON block (data-lsc-designs) and the DOM. The 3D cover is <legstyle-cover-viewer>
 * (assets/legstyle-cover-viewer.js); the drawing tool is not loaded here (it lives on the Make It Yours page).
 *
 * Section reloads (theme editor): connectedCallback sets up, disconnectedCallback removes every listener.
 */

class LegStyleChooseCover extends HTMLElement {
  connectedCallback() { this.init(); }
  disconnectedCallback() { this.teardown(); }

  async init() {
    if (this._live) return;
    this._live = true;
    const gen = this._gen = (this._gen || 0) + 1;
    this._ac = new AbortController();
    const signal = this._ac.signal;
    try { this._data = JSON.parse(this.querySelector('script[data-lsc-designs]').textContent); }
    catch { return; }                                        // no data: the plain HTML still works
    this._tablist = this.querySelector('[data-ls-tablist]');
    this._tabs = [...this.querySelectorAll('[role="tab"]')];
    this._panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this._cards = [...this.querySelectorAll('a.lsc-card[data-design]')];
    this._radios = [...this.querySelectorAll('input.lsc-sw__input')];
    this._colourName = this.querySelector('[data-ls-colour-name]');
    this._pickName = this.querySelector('[data-ls-pick-name]');
    this._pickColour = this.querySelector('[data-ls-pick-colour]');
    this._lineName = this.querySelector('[data-ls-pickline-name]');        // phones: the choice under the cover
    this._lineColour = this.querySelector('[data-ls-pickline-colour]');
    this._form = this.querySelector('[data-ls-form]');
    this._copyBtn = this.querySelector('[data-ls-copy]');
    this._shareBtn = this.querySelector('[data-ls-share]');
    this._copied = this.querySelector('[data-ls-copied]');
    this._viewer = this.querySelector('legstyle-cover-viewer');

    // tabs: shown once JavaScript runs; panels other than the chosen one are hidden
    this.classList.add('lsc-js');
    if (this._tablist) this._tablist.hidden = false;
    // Phones (Andre, 9 Oct 2026): the themes sit in the pinned panel, left of the cover (colours on the right), so
    // only the designs scroll. The same tab buttons move there and back with the breakpoint; nothing is copied.
    // Astra, 9 Oct night: a class (not :has) sets the three columns, so older phones lay it out too; the list always
    // goes back before "Or make it your own" (or the first designs), so a reconnect cannot lose its place; on phones
    // it is a vertical tab list (arrow keys up and down as well).
    const panel = this.querySelector('.lsc-viewer'), steps = this.querySelector('.lsc-steps');
    if (this._tablist && panel && steps) {
      const mq = window.matchMedia('(max-width: 899px)');
      const place = () => {
        const phone = mq.matches;
        if (phone && this._tablist.parentNode !== panel) panel.prepend(this._tablist);
        if (!phone && this._tablist.parentNode !== steps) {
          steps.insertBefore(this._tablist, steps.querySelector(':scope > .lsc-own-link, :scope > .lsc-panel'));
        }
        panel.classList.toggle('lsc-has-side', phone);
        if (phone) this._tablist.setAttribute('aria-orientation', 'vertical'); else this._tablist.removeAttribute('aria-orientation');
      };
      place();
      if (mq.addEventListener) mq.addEventListener('change', place, { signal });
      else if (mq.addListener) { mq.addListener(place); signal.addEventListener('abort', () => mq.removeListener(place)); }
    }
    this._tablist && this._tablist.addEventListener('click', (e) => {
      const t = e.target.closest('[role="tab"]'); if (t) this.selectTab(+t.dataset.tab, { userAction: true });
    }, { signal });
    this._tablist && this._tablist.addEventListener('keydown', (e) => this._onTabKey(e), { signal });
    this.addEventListener('click', (e) => this._onCardClick(e), { signal });
    this.addEventListener('contextmenu', (e) => { if (e.target.closest('.lsc-card')) e.preventDefault(); }, { signal });
    for (const r of this._radios) r.addEventListener('change', () => { if (r.checked) this.selectColour(r.value, { userAction: true }); }, { signal });
    if (this._copyBtn) { this._copyBtn.hidden = false; this._copyBtn.addEventListener('click', () => this._copyForm(), { signal }); }
    if (this._shareBtn) { this._shareBtn.hidden = false; this._shareBtn.addEventListener('click', () => this._share(), { signal }); }

    // the viewer element is defined by its own module; wait for it, then for its connectedCallback
    await customElements.whenDefined('legstyle-cover-viewer');
    await Promise.resolve();
    if (gen !== this._gen || !this._live) return;

    // a shared link (?design=...&colour=...) opens on that choice
    const q = new URLSearchParams(location.search);
    const qDesign = q.get('design'), qColour = (q.get('colour') || '').toUpperCase();
    const design = qDesign && this._data.designs[qDesign] ? qDesign : this._data.opening;
    const named = this._radios.find((r) => r.value === qColour);
    const radio = named || this._radios.find((r) => r.checked) || this._radios[0];
    if (radio) { radio.checked = true; this.selectColour(radio.value); }
    let tab = this._data.tabs.findIndex((t) => t.designs.includes(design));
    if (tab < 0) tab = 0;
    this.selectTab(tab, { keepDesign: true });
    if (design) this.selectDesign(design, { initial: design === this._data.opening });
    // a clinician's link: the panel shows exactly what the link says, and says so when it is missing or unknown
    if (q.get('for') === 'clinician') {
      this._clinician = true;
      this._showClinician(qDesign && this._data.designs[qDesign] ? qDesign : null, named ? radio : null);
    }
  }

  teardown() {
    if (!this._live) return;
    this._live = false;
    this._gen++;
    if (this._ac) this._ac.abort();
    clearTimeout(this._copiedTimer);
  }

  /* ---------- tabs ---------- */
  selectTab(i, { userAction = false, focus = false, keepDesign = false } = {}) {
    if (!this._tabs[i]) return;
    this._tabs.forEach((t, j) => { t.setAttribute('aria-selected', String(j === i)); t.tabIndex = j === i ? 0 : -1; });
    this._panels.forEach((p, j) => { p.hidden = j !== i; });
    if (focus) this._tabs[i].focus();
    this._tab = i;
    if (keepDesign) return;
    // as the prototype: a tab that does not hold the current design shows its first design
    const list = (this._data.tabs[i] || {}).designs || [];
    if (list.length && !list.includes(this._design)) this.selectDesign(list[0], { userAction });
  }
  _onTabKey(e) {
    const i = this._tabs.indexOf(e.target.closest('[role="tab"]')); if (i < 0) return;
    const n = this._tabs.length;
    const to = { ArrowRight: (i + 1) % n, ArrowDown: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, ArrowUp: (i - 1 + n) % n,
      Home: 0, End: n - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    this.selectTab(to, { userAction: true, focus: true });
  }

  /* ---------- designs ---------- */
  _onCardClick(e) {
    const a = e.target.closest('a.lsc-card[data-design]'); if (!a || !this.contains(a)) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;   // let "open in a new tab" work
    e.preventDefault();
    this.selectDesign(a.dataset.design, { userAction: true });
  }
  async selectDesign(handle, { initial = false, userAction = false } = {}) {
    const d = this._data.designs[handle]; if (!d) return;
    this._design = handle;
    for (const c of this._cards) {
      if (c.dataset.design === handle) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
    }
    if (userAction) this._writeUrl();
    // While the still image shows (3D not up, or not available), the still changes at once, so the name does too.
    // On the 3D cover the name changes only when that design's map is on the cover.
    const still = !this._viewer || this._viewer.dataset.state !== '3d';
    if (initial || still) { this._shown = handle; this._updateChoice(); }
    else this._setLoading(d);
    const res = this._viewer
      ? await this._viewer.showMap({ url: d.map, base: d.base, amp: d.amp, still: d.still,
        alt: d.desc ? `${d.name} on a cover. ${d.desc}` : `${d.name} on a cover` })
      : 'still';
    if (!this._live || this._design !== handle || res === 'superseded') return;   // a later tap has taken over
    if (res === 'error') { this._setError(d); return; }
    this._shown = handle;
    this._updateChoice();
  }

  /* ---------- colours ---------- */
  selectColour(code, { userAction = false } = {}) {
    const r = this._radios.find((x) => x.value === code); if (!r) return;
    this._colour = { code, name: r.dataset.name, hex: r.dataset.hex };
    if (this._colourName) { this._colourName.replaceChildren(Object.assign(document.createElement('b'), { textContent: code }), ' ' + r.dataset.name); }
    if (this._viewer && this._viewer.setColour) this._viewer.setColour(r.dataset.hex);
    if (userAction) this._writeUrl();
    this._updateChoice();
  }

  /* ---------- the choice summary ---------- */
  _colourText() { return this._colour ? `${this._colour.code} ${this._colour.name}` : ''; }
  _updateChoice() {
    const d = this._data && this._data.designs[this._shown || ''];
    if (!d || !this._form) return;
    this._pickName.textContent = d.name;
    this._pickColour.textContent = this._colourText();
    if (this._lineName) this._lineName.textContent = d.name;
    if (this._lineColour) this._lineColour.textContent = this._colour ? `in ${this._colour.name}` : '';
    this._form.textContent = `For the order form: Style: ${d.name} · Colour: ${this._colourText()}`;
    if (this._copyBtn) this._copyBtn.disabled = false;
    const shown = this.querySelector('[data-ls-message]'); if (shown) shown.hidden = true;   // a new choice: copy again
  }
  _setLoading(d) {
    if (this._form) this._form.textContent = `Loading ${d.name}...`;
    if (this._copyBtn) this._copyBtn.disabled = true;
  }
  _setError(d) {
    if (this._form) this._form.textContent = `Could not load ${d.name}. Tap it again to try again.`;
    if (this._copyBtn) this._copyBtn.disabled = true;
  }
  _flash(msg) {
    this._copied.textContent = msg;
    clearTimeout(this._copiedTimer);
    // long enough to read (and copy the email address from) a longer message: 6 s, or about 80 ms a character
    this._copiedTimer = setTimeout(() => { if (this._copied) this._copied.textContent = ''; }, Math.max(6000, msg.length * 80));
  }
  /* the note a wearer sends their clinician, ready to send as it is (Andre, 10 Oct 2026, after launch): it said only
     "Style: ... · Colour: ...". The two lines stay exact, for the order form. */
  _message() {
    const d = this._data && this._data.designs[this._design || ''];     // the same design as the link
    if (!d) return '';
    const photos = this.querySelector('[data-ls-photos]');
    return [
      'Hi,', '',
      "I've been looking at LegStyle covers, and this is the one I'd like:", '',
      `Style: ${d.name}`,
      `Colour: ${this._colourText()}`, '',
      `You can see it here: ${this.shareUrl(true)}`, '',
      // each answer names the order form's own box (section 7), so the clinician can carry it across
      photos && photos.checked
        ? "I'm happy for LegStyle to show photos of my finished cover (the cover only, never me). On the order form: Yes, share this cover."
        : 'Please keep my cover private: no photos of it. On the order form: Keep this cover private.', '',
      'Thank you',
    ].join('\n');
  }
  async _copyForm() {
    const txt = this._message();
    if (!txt) return;
    try { await navigator.clipboard.writeText(txt); this._flash('Copied. Paste it into an email or message to your clinician.'); }
    catch { this._showMessage(txt); }
  }
  /* no clipboard here: show the whole message, selected, to copy by hand (legstyle-reviewer, 10 Oct) */
  _showMessage(txt) {
    let box = this.querySelector('[data-ls-message]');
    if (!box) {
      box = Object.assign(document.createElement('pre'), { className: 'lsc-message' });
      box.setAttribute('data-ls-message', '');
      this._copied.closest('.lsc-row').after(box);
    }
    box.textContent = txt; box.hidden = false;
    const photos = this.querySelector('[data-ls-photos]');      // a changed answer makes it out of date
    if (photos) photos.addEventListener('change', () => { box.hidden = true; }, { once: true, signal: this._ac.signal });
    this._select(box); this._flash('Selected: copy it with your keyboard or a long press');
  }
  _select(el) { const r = document.createRange(); r.selectNodeContents(el); const s = getSelection(); s.removeAllRanges(); s.addRange(r); }

  /* ---------- shareable link ---------- */
  /* the address of this choice: here (for the browser's address bar), or the Choose your cover page with
     for=clinician (for the link sent to a clinician, which opens on the clinician panel) */
  shareUrl(forClinician = false) {
    const u = forClinician && this.dataset.shareUrl ? new URL(this.dataset.shareUrl, location.origin) : new URL(location.href);
    u.hash = '';
    for (const k of ['design', 'colour', 'for']) u.searchParams.delete(k);
    if (this._design) u.searchParams.set('design', this._design);
    if (this._colour) u.searchParams.set('colour', this._colour.code);
    if (forClinician) u.searchParams.set('for', 'clinician');
    return u.toString();
  }
  _writeUrl() {
    if (this._clinician) return;     // a clinician's link stays as sent, so reloading it shows the patient's choice
    try { history.replaceState(history.state, '', this.shareUrl()); } catch { /* sandboxed: the share button still works */ }
  }
  /* ---------- the clinician panel (a shared link with for=clinician) ----------
     It records what the link says and does not follow later taps below (Astra, 9 Oct night: it went stale). A design
     or colour the link does not name, or that we do not have, is said plainly; the copy line has only what is known. */
  _showClinician(handle, radio) {
    const panel = this.querySelector('[data-ls-clinician]');
    if (!panel) return;
    const $ = (s) => panel.querySelector(s), d = handle ? this._data.designs[handle] : null;
    const img = $('[data-ls-cl-img]'), msg = $('[data-ls-cl-msg]');
    const colour = radio ? `${radio.value} ${radio.dataset.name}` : '';
    let line = '';
    if (!d) {
      img.hidden = true;
      $('.lsc-clinician__pick').hidden = true;
      $('[data-ls-cl-copy]').hidden = true;
      msg.textContent = 'This link does not name a design we have. Ask your patient to send their choice again, or look at the designs below.';
      msg.hidden = false;
    } else {
      line = radio ? `Style: ${d.name} · Colour: ${colour}` : `Style: ${d.name}`;
      if (d.still || d.image) { img.src = d.still || d.image; img.alt = d.desc ? `${d.name} on a cover. ${d.desc}` : `${d.name} on a cover`; }
      else img.hidden = true;
      $('[data-ls-cl-name]').textContent = d.name;
      $('[data-ls-cl-colour]').textContent = radio ? radio.dataset.name : 'a colour the link does not give';
      if (!radio) {
        msg.textContent = 'The link does not give one of our colours. Ask your patient, or look at the colours below.';
        msg.hidden = false;
      }
    }
    $('[data-ls-cl-form]').textContent = line;
    $('[data-ls-cl-form]').hidden = !line;
    $('[data-ls-cl-formlabel]').hidden = !line;
    $('[data-ls-cl-copy]').addEventListener('click', async () => {
      const out = $('[data-ls-cl-copied]');
      try { await navigator.clipboard.writeText(line); out.textContent = 'Copied'; }
      catch { this._select($('[data-ls-cl-form]')); out.textContent = 'Selected: copy it with your keyboard or a long press'; }
    }, { signal: this._ac.signal });
    panel.hidden = false;
    this.classList.add('lsc-for-clinician');
  }
  async _share() {
    const url = this.shareUrl(true), text = this._message();
    if (navigator.share) {
      // the link is inside the message, so it is not passed again (some apps would show it twice)
      try { await navigator.share(text ? { title: 'My LegStyle cover', text } : { title: 'LegStyle', url }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(text || url); this._flash('Copied. Paste it into an email or message to your clinician.'); }
    catch { if (text) this._showMessage(text); else this._flash(url); }
  }
}

if (!customElements.get('legstyle-choose-cover')) customElements.define('legstyle-choose-cover', LegStyleChooseCover);
